import React, { useState } from 'react'
import { fetchWithCsrf } from '../utils/csrf'
import ConfirmDialog from './ConfirmDialog'
import Modal from './Modal'

interface InviteModalProps {
  clubId: number
  clubName: string
  inviteToken: string
  onClose: () => void
  onTokenRegenerated: (newToken: string) => void
}

export default function InviteModal({
  clubId,
  clubName,
  inviteToken,
  onClose,
  onTokenRegenerated
}: InviteModalProps) {
  const [copied, setCopied] = useState(false)
  const [regenerating, setRegenerating] = useState(false)
  const [showRegenerateConfirm, setShowRegenerateConfirm] = useState(false)

  const inviteUrl = `${window.location.origin}/join/${inviteToken}`

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(inviteUrl)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }

  const handleRegenerateToken = () => {
    setShowRegenerateConfirm(true)
  }

  const handleRegenerateConfirmed = async () => {
    setShowRegenerateConfirm(false)
    setRegenerating(true)
    try {
      const response = await fetchWithCsrf(`/api/v1/clubs/${clubId}/regenerate_invite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      })

      if (response.ok) {
        const data = await response.json()
        onTokenRegenerated(data.invite_token)
      } else {
        console.error('Failed to regenerate invite token')
      }
    } catch (err) {
      console.error('Error regenerating invite token:', err)
    } finally {
      setRegenerating(false)
    }
  }

  return (
    <>
      <Modal isOpen={true} onClose={onClose} size="sm" showCloseButton={true}>
        <div className="p-8">
          <div className="mb-6">
          <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-xl mb-4">
            <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Invite Members</h2>
          <p className="text-gray-600">Share this link to invite people to {clubName}</p>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Invite Link
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={inviteUrl}
                readOnly
                className="flex-1 px-4 py-3 border border-gray-300 rounded-lg bg-gray-50 text-sm text-gray-600 focus:outline-none"
                onClick={(e) => e.currentTarget.select()}
              />
              <button
                onClick={handleCopyLink}
                className="px-4 py-3 bg-gradient-to-r from-indigo-500 to-purple-500 text-white font-semibold rounded-lg hover:from-indigo-600 hover:to-purple-600 transition-all shadow-lg shadow-indigo-500/30 whitespace-nowrap"
              >
                {copied ? (
                  <span className="flex items-center gap-2">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Copied!
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                    Copy
                  </span>
                )}
              </button>
            </div>
          </div>

          <div className="pt-4 border-t border-gray-200">
            <button
              onClick={handleRegenerateToken}
              disabled={regenerating}
              className="text-sm text-gray-600 hover:text-gray-900 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {regenerating ? 'Regenerating...' : 'Regenerate invite link'}
            </button>
            <p className="text-xs text-gray-500 mt-2">
              This will invalidate the old link and create a new one
            </p>
          </div>
        </div>
        </div>
      </Modal>

      {showRegenerateConfirm && (
        <ConfirmDialog
          message="This will invalidate the old invite link. Are you sure?"
          onConfirm={handleRegenerateConfirmed}
          onCancel={() => setShowRegenerateConfirm(false)}
          confirmText="Regenerate"
          confirmButtonClass="bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600"
        />
      )}
    </>
  )
}
